import { SnitcherBody } from './Snitcher';
import { GTMBody } from './GTM';
import { FacebookPixelsBody } from './FacebookTracker';
import { LinkedInTrackerBody } from './LinkedInTracker';
import { PinterestTrackerBody } from './PinterestTracker';
import type { ClientConfig } from '@archipro-website/config';
import { useRootData } from '@modules/root';
import { shouldAllowAdvertisingPixels } from './allow-advertising-pixels';
import { isConfiguredPixelId } from './is-configured-pixel-id';

/**
 * Server-side rendered body pixels. The Facebook, Pinterest, and LinkedIn entries are the
 * `<noscript><img>` halves of those trackers. When the market honours GPC we
 * also read the HTTP `Sec-GPC` header so no-JS requests with an active signal
 * do not emit advertising pixels. The `<script>` halves live in
 * `ClientAdvertisingPixels` and are gated on `navigator.globalPrivacyControl`.
 *
 * GTM noscript is gated by `isConfiguredPixelId` so placeholder or empty IDs
 * from client config do not emit a broken iframe. Production configs carry real
 * GTM IDs; team/staging configs should too if no-JS GTM fallback is needed.
 */
export const BodyPixels = ({
    gtmId,
    snitcherTrackingId,
    facebookPixelArchiproId,
    facebookPixelClientId,
    pinterestTagArchiproId,
    pinterestTagClientId,
    linkedinPartnerId,
}: Pick<
    ClientConfig['pixels'],
    | 'gtmId'
    | 'snitcherTrackingId'
    | 'facebookPixelArchiproId'
    | 'facebookPixelClientId'
    | 'pinterestTagArchiproId'
    | 'pinterestTagClientId'
    | 'linkedinPartnerId'
>) => {
    const { localisation, globalPrivacyControl } = useRootData();
    const allowAdvertising = shouldAllowAdvertisingPixels(
        localisation.countryConfig.honourGlobalPrivacyControl,
        globalPrivacyControl,
    );

    const hasFacebookPixel =
        isConfiguredPixelId(facebookPixelArchiproId) ||
        isConfiguredPixelId(facebookPixelClientId);
    const hasPinterestPixel =
        isConfiguredPixelId(pinterestTagArchiproId) ||
        isConfiguredPixelId(pinterestTagClientId);

    return (
        <>
            {snitcherTrackingId && <SnitcherBody id={snitcherTrackingId} />}
            {isConfiguredPixelId(gtmId) && <GTMBody id={gtmId} />}
            {allowAdvertising && hasFacebookPixel && (
                <FacebookPixelsBody
                    pixel={facebookPixelArchiproId}
                    pixelClient={facebookPixelClientId}
                />
            )}
            {allowAdvertising && hasPinterestPixel && (
                <PinterestTrackerBody
                    pixelArchipro={pinterestTagArchiproId}
                    pixelClient={pinterestTagClientId}
                />
            )}
            {allowAdvertising && isConfiguredPixelId(linkedinPartnerId) && (
                <LinkedInTrackerBody partnerId={linkedinPartnerId} />
            )}
        </>
    );
};
